import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { z } from "zod";
import { useAuth } from "../useAuth";
import { ChiefAPIError } from "@/backend/types/backendTypes";
import axiosInstance from "@lib/axios";

const CheckConnectionsSchema = z.object({
  organisation_id: z.string(),
  source: z.enum([
    "facebook",
    "google",
    "klaviyo",
    "pinterest",
    "tiktok",
    "outbrain",
    "msads",
    "taboola",
  ]),
});

export type CheckConnectionsInput = z.infer<typeof CheckConnectionsSchema>;

const CheckConnectionsResponseSchema = z.array(
  z.object({
    account_id: z.string(),
    source: z.string(),
    is_valid: z.boolean(),
    reference_id: z.string(),
  })
);

export type CheckConnectionsResponse = z.infer<
  typeof CheckConnectionsResponseSchema
>;

const onCheckConnections = async (
  input: CheckConnectionsInput
): Promise<CheckConnectionsResponse> => {
  try {
    CheckConnectionsSchema.parse(input);
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/account/check-connections`,
      input
    );

    if (response.status !== 200) {
      throw new Error(response?.data?.error ?? "Unknown error occurred.");
    }

    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      if (!err.response) {
        throw err;
      }
      const axiosError = err as AxiosError;
      const apiError = axiosError.response?.data as ChiefAPIError;
      const firstErr = apiError.detail?.[0];
      throw new Error(firstErr?.msg ?? axiosError.message);
    }
    throw new Error("Unknown error occurred!");
  }
};

export const useCheckConnections = () => {
  return useMutation({
    mutationFn: (input: CheckConnectionsInput) => onCheckConnections(input),
    // Don't invalidate queries - we don't want to reload ad accounts data
  });
};
